/**
 * CRM leads — read-only swimlane board (drag to pan horizontally)
 */

import React, { useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDbDateTime } from '../../../utils/datetime'
import {
  getCrmBoardStatuses,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
  statusBadgeClass,
} from '../../../lib/crm-display'

function LeadCard({ lead }) {
  const navigate = useNavigate()

  return (
    <button
      type="button"
      onClick={() => navigate(`/hub/crm/${lead.id}`)}
      className={[
        'w-full text-left rounded-lg border p-3 shadow-sm transition-shadow',
        'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-500/40',
        lead.is_idle ? 'border-amber-200 bg-amber-50/80' : 'border-gray-200 bg-white',
      ].join(' ')}
    >
      <p className="font-medium text-green-800 text-sm leading-snug">{lead.display_name}</p>
      <p className="text-xs text-gray-500 mt-1 truncate">
        {LEAD_SOURCE_LABELS[lead.source] || lead.source}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
        <span className="truncate max-w-full">
          {lead.assignee_name || <span className="text-gray-400">Unassigned</span>}
        </span>
        {lead.days_idle != null ? (
          <span className={lead.is_idle ? 'font-semibold text-amber-800' : ''}>
            · {lead.days_idle}d idle
          </span>
        ) : null}
      </div>
      {lead.last_activity_at ? (
        <p className="text-[11px] text-gray-400 mt-1.5">
          {formatDbDateTime(lead.last_activity_at)}
        </p>
      ) : null}
    </button>
  )
}

function isInteractiveTarget(target) {
  return Boolean(
    target.closest('button, a, input, select, textarea, [role="button"], [data-no-pan]')
  )
}

export default function CrmLeadsBoard({
  leads,
  statusFilter = '',
  includeArchived = false,
  capped = false,
}) {
  const scrollRef = useRef(null)
  const panRef = useRef({ active: false, startX: 0, scrollLeft: 0, pointerId: null })

  const lanes = useMemo(() => {
    const statuses = getCrmBoardStatuses({ includeArchived, statusFilter })
    const byStatus = Object.fromEntries(statuses.map((s) => [s, []]))
    for (const lead of leads) {
      if (byStatus[lead.status]) byStatus[lead.status].push(lead)
    }
    return statuses.map((status) => ({
      status,
      label: LEAD_STATUS_LABELS[status] || status,
      leads: byStatus[status] || [],
    }))
  }, [leads, statusFilter, includeArchived])

  useEffect(() => {
    const endPan = () => {
      if (!panRef.current.active) return
      panRef.current.active = false
      panRef.current.pointerId = null
      const el = scrollRef.current
      if (el) {
        el.classList.remove('cursor-grabbing')
        el.classList.add('cursor-grab')
      }
    }

    const onPointerMove = (event) => {
      if (!panRef.current.active || event.pointerId !== panRef.current.pointerId) return
      const el = scrollRef.current
      if (!el) return
      const dx = event.clientX - panRef.current.startX
      el.scrollLeft = panRef.current.scrollLeft - dx
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', endPan)
    window.addEventListener('pointercancel', endPan)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', endPan)
      window.removeEventListener('pointercancel', endPan)
    }
  }, [])

  const onPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return
    if (isInteractiveTarget(event.target)) return

    const el = scrollRef.current
    if (!el) return

    panRef.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
      pointerId: event.pointerId,
    }
    el.classList.remove('cursor-grab')
    el.classList.add('cursor-grabbing')
    el.setPointerCapture?.(event.pointerId)
  }

  return (
    <div className="space-y-2">
      {capped ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Board shows up to 200 cases. Use filters or the List tab for pagination.
        </p>
      ) : null}
      <p className="text-xs text-gray-500">
        Drag the board (column headers or gaps) to scroll sideways. Click a card to open the case.
      </p>

      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        className="overflow-x-auto pb-2 -mx-1 px-1 cursor-grab select-none touch-pan-y"
      >
        <div className="flex gap-3 min-w-max items-start">
          {lanes.map((lane) => (
            <section
              key={lane.status}
              className="w-[272px] shrink-0 flex flex-col max-h-[calc(100vh-280px)] min-h-[200px] rounded-xl border border-gray-200 bg-gray-50/80"
            >
              <header className="shrink-0 px-3 py-2.5 border-b border-gray-200 bg-white rounded-t-xl">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(lane.status)}`}
                  >
                    {lane.label}
                  </span>
                  <span className="text-xs font-medium text-gray-500 tabular-nums">
                    {lane.leads.length}
                  </span>
                </div>
              </header>
              <div className="flex-1 overflow-y-auto p-2 space-y-2 overscroll-y-contain">
                {lane.leads.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-6 px-2">No cases</p>
                ) : (
                  lane.leads.map((lead) => (
                    <div key={lead.id} data-no-pan>
                      <LeadCard lead={lead} />
                    </div>
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  )
}
